import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, StatusBadge, EmptyState } from "@/components/kit";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dumbbell, Plus, MoreVertical, Copy, Pause, Square, Trash2, Pencil } from "lucide-react";
import MediaUpload from "@/components/MediaUpload";

const empty = {
  client_id: "", name: "", objective: "", description: "", instructions: "",
  audio_url: "", video_url: "", pdf_url: "", duration: "", frequency: "",
  start_date: "", end_date: "", notes: "",
};

export default function Exercises() {
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/exercises").then(({ data }) => setList(data));
  useEffect(() => { load(); api.get("/clients").then(({ data }) => setClients(data)); }, []);

  const openNew = () => { setEditing(null); setForm({ ...empty, client_id: clients[0]?.id || "" }); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ ...empty, ...e }); setOpen(true); };

  const save = async () => {
    if (!form.client_id) { toast.error("Selecione um cliente"); return; }
    try {
      if (editing) await api.put(`/exercises/${editing.id}`, form);
      else await api.post("/exercises", form);
      toast.success("Exercício salvo"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => { await api.patch(`/exercises/${id}/status`, { status }); toast.success("Atualizado"); load(); };
  const dup = async (id) => { await api.post(`/exercises/${id}/duplicate`); toast.success("Duplicado"); load(); };
  const del = async (id) => { await api.delete(`/exercises/${id}`); toast.success("Removido"); load(); };

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  return (
    <div>
      <PageHeader title="Exercícios" subtitle="Crie e acompanhe exercícios personalizados"
        action={<Button onClick={openNew} className="rounded-full" disabled={clients.length === 0} data-testid="new-exercise-btn"><Plus className="w-4 h-4 mr-1" />Novo exercício</Button>} />
      {clients.length === 0 && <p className="text-sm text-muted-foreground mb-4">Cadastre um cliente antes de criar exercícios.</p>}

      {list.length === 0 ? <EmptyState icon={Dumbbell} title="Nenhum exercício" desc="Crie exercícios personalizados para seus clientes." /> : (
        <div className="space-y-3">
          {list.map((e) => (
            <Card key={e.id} className="p-5" data-testid={`exercise-row-${e.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{e.name}</span>
                    <StatusBadge status={e.status} />
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">Cliente: {clientName(e.client_id)} · {e.frequency || "—"} · {e.duration || "—"}</p>
                  {e.objective && <p className="text-sm text-muted-foreground">Objetivo: {e.objective}</p>}
                  <div className="mt-3 max-w-xs">
                    <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{e.completion_count} realização(ões)</span><span>{e.progress}%</span></div>
                    <Progress value={e.progress} className="h-2" />
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`exercise-menu-${e.id}`}><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openEdit(e)}><Pencil className="w-4 h-4 mr-2" />Editar</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => dup(e.id)}><Copy className="w-4 h-4 mr-2" />Duplicar</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setStatus(e.id, "closed")}><Square className="w-4 h-4 mr-2" />Encerrar</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setStatus(e.id, "pending")}><Pause className="w-4 h-4 mr-2" />Marcar pendente</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => del(e.id)} className="text-destructive"><Trash2 className="w-4 h-4 mr-2" />Excluir</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar exercício" : "Novo exercício"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Cliente</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger className="mt-1" data-testid="exercise-client-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="exercise-name-input" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Objetivo</Label><Input value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} className="mt-1" /></div>
              <div><Label>Tempo estimado</Label><Input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="10 minutos" className="mt-1" /></div>
            </div>
            <div><Label>Descrição</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
            <div><Label>Instruções</Label><Textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Frequência</Label><Input value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} placeholder="1 vez ao dia" className="mt-1" /></div>
              <div><Label>Observações</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data inicial</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" /></div>
              <div><Label>Data final</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <MediaUpload label="Áudio" accept="audio/*" value={form.audio_url} onChange={(v) => setForm({ ...form, audio_url: v })} testid="ex-audio" />
              <MediaUpload label="Vídeo" accept="video/*" value={form.video_url} onChange={(v) => setForm({ ...form, video_url: v })} testid="ex-video" />
              <MediaUpload label="PDF" accept="application/pdf" value={form.pdf_url} onChange={(v) => setForm({ ...form, pdf_url: v })} testid="ex-pdf" />
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-exercise-btn">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
